import { useMemo, type KeyboardEvent } from 'react'
import { useNavigate } from 'react-router-dom'

import campaignsJson from '../../demo/fixtures/campaigns.json'
import enumsJson from '../../demo/fixtures/enums.json'
import { formatMultiple, formatPercent, formatUsd } from '../../demo/format'
import { useDemoStore } from '../../demo/store'
import type { CampaignStatus, Market, Objective, Product } from '../../demo/types'
import { Badge, Button, Card, CardHead, Icon } from '../../ui'
import { selectRecentCampaigns, summarizeCampaignAds } from './overview'
import styles from './RecentTasksCard.module.css'

type BadgeTone = 'green' | 'red' | 'orange' | 'gray' | 'blue'

interface CampaignFixture {
  products: Product[]
}

interface EnumFixture {
  objective: Record<Objective, string>
  market: Record<Market, string>
  status: Record<CampaignStatus, string>
  statusTone: Record<CampaignStatus, BadgeTone>
}

const campaignFixture = campaignsJson as unknown as CampaignFixture
const enumFixture = enumsJson as unknown as EnumFixture
const productsById = new Map(campaignFixture.products.map((product) => [product.id, product]))

export function RecentTasksCard() {
  const navigate = useNavigate()
  const campaigns = useDemoStore((state) => state.campaigns)
  const ads = useDemoStore((state) => state.ads)
  const rows = useMemo(() => selectRecentCampaigns(campaigns), [campaigns])

  const openFromKeyboard = (event: KeyboardEvent<HTMLDivElement>, id: string) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      void navigate(`/monitor/${id}`)
    }
  }

  return (
    <Card data-scope="overview-recent">
      <CardHead title="最近广告任务" action={<Button className={styles.cardLink} variant="link" onClick={() => { void navigate('/monitor') }}>查看全部 →</Button>} />
      {rows.map((campaign) => {
        const product = productsById.get(campaign.productId)
        const summary = summarizeCampaignAds(ads, campaign.id)
        const ctr = summary.impressions === 0 ? null : summary.clicks / summary.impressions
        const cvr = summary.clicks === 0 ? null : campaign.orders / summary.clicks
        const market = campaign.markets[0]
        const metrics = [
          ['消耗', formatUsd(campaign.spendMonth)],
          ['CTR', ctr === null ? '—' : formatPercent(ctr)],
          ['CVR', cvr === null ? '—' : formatPercent(cvr)],
          ['ROAS', campaign.metrics.roas === null ? '—' : formatMultiple(campaign.metrics.roas)],
        ]
        return (
          <div
            className={styles.row}
            key={campaign.id}
            role="button"
            tabIndex={0}
            aria-label={`查看 ${campaign.adName}`}
            onClick={() => { void navigate(`/monitor/${campaign.id}`) }}
            onKeyDown={(event) => openFromKeyboard(event, campaign.id)}
          >
            <img className={styles.thumb} src={`/${product?.image ?? ''}`} alt={product?.name ?? campaign.adName} />
            <div className={styles.nameBlock}>
              <p className={styles.name}>{campaign.adName}</p>
              <p className={styles.sub}>{enumFixture.objective[campaign.objective]} · {market ? enumFixture.market[market] : '—'}</p>
            </div>
            <Badge className={styles.status} tone={enumFixture.statusTone[campaign.status]}>{enumFixture.status[campaign.status]}</Badge>
            <div className={styles.metrics}>
              {metrics.map(([key, value]) => (
                <div className={styles.metric} key={key}>
                  <p className={styles.metricKey}>{key}</p>
                  <p className={styles.metricValue}>{value}</p>
                </div>
              ))}
            </div>
            <Icon className={styles.chevron} name="chevron-right" size={18} strokeWidth={2} />
          </div>
        )
      })}
    </Card>
  )
}
